import ComparisonOperator from '../../../../types/BotBuilder/ComparisonOperator';
import SlotValueType from '../../../../types/BotBuilder/SlotValueType';

export type ConditionalOperatorOption = {
  value: ComparisonOperator;
  label: string;
};

export const operatorRequiresComparisonValue = (
  operator: ComparisonOperator | null
): boolean =>
  !!operator &&
  operator !== ComparisonOperator.IS_NULL &&
  operator !== ComparisonOperator.BOOLEAN;

export const getConditionalOperatorOptions = (
  variableType?: SlotValueType
): ConditionalOperatorOption[] => {
  const defaultOptions = [
    {
      value: ComparisonOperator.EQUALS,
      label: 'Equals'
    },
    { value: ComparisonOperator.IS_NULL, label: 'No value' }
  ];

  if (variableType === SlotValueType.BOOLEAN) {
    return [
      {
        value: ComparisonOperator.BOOLEAN,
        label: 'Is true'
      },
      { value: ComparisonOperator.IS_NULL, label: 'No value' }
    ];
  }

  if (variableType === SlotValueType.NUMBER) {
    return [
      ...defaultOptions,
      { value: ComparisonOperator.LESS_THAN, label: '<' },
      { value: ComparisonOperator.LESS_THAN_OR_EQUAL_TO, label: '<=' },
      { value: ComparisonOperator.GREATER_THAN, label: '>' },
      { value: ComparisonOperator.GREATER_THAN_OR_EQUAL_TO, label: '>=' }
    ];
  }

  if (variableType === SlotValueType.DATE) {
    return [
      { value: ComparisonOperator.EQUALS, label: 'On' },
      { value: ComparisonOperator.LESS_THAN, label: 'Before' },
      {
        value: ComparisonOperator.LESS_THAN_OR_EQUAL_TO,
        label: 'On or before'
      },
      { value: ComparisonOperator.GREATER_THAN, label: 'After' },
      {
        value: ComparisonOperator.GREATER_THAN_OR_EQUAL_TO,
        label: 'On or after'
      }
    ];
  }

  return defaultOptions;
};

export const normaliseConditionalState = (
  variableType: SlotValueType | undefined,
  operator: ComparisonOperator | null,
  comparisonValue: string | null
): {
  operator: ComparisonOperator;
  comparisonValue: string;
} => {
  const validOperators = getConditionalOperatorOptions(variableType).map(
    (option) => option.value
  );
  const fallbackOperator = validOperators[0] ?? ComparisonOperator.EQUALS;
  const normalisedOperator =
    operator && validOperators.includes(operator)
      ? operator
      : fallbackOperator;

  return {
    operator: normalisedOperator,
    comparisonValue: operatorRequiresComparisonValue(normalisedOperator)
      ? (comparisonValue ?? '')
      : ''
  };
};
